import React, { useEffect, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { FaMusic, FaGamepad, FaChartLine, FaHeadphones, FaPlay, FaHeart, FaStar, FaLanguage } from 'react-icons/fa';
import gsap from 'gsap';

const Features: React.FC = () => {
  // Features data with song card styling
  const features = [
    {
      id: 1,
      icon: <FaMusic className="w-8 h-8 text-white" />,
      title: "Learn English with Real Music",
      description: "Master English through popular songs you already love. Lyrics become your learning material.",
      image: "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/Screenshot%202025-08-11%20203222.png",
      color: "red",
      gradient: "from-red-500/80 to-red-700/80",
      hoverGradient: "from-red-400/90 to-red-600/90",
      delay: 0,
      likes: "2.4K",
      language: "English"
    },
    {
      id: 2,
      icon: <FaGamepad className="w-8 h-8 text-white" />,
      title: "Practice Mode for Exercises",
      description: "Interactive exercises based on song lyrics help reinforce vocabulary and grammar.",
      image: "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/Screenshot%202025-08-11%20203257.png",
      color: "purple",
      gradient: "from-purple-500/80 to-purple-700/80",
      hoverGradient: "from-purple-400/90 to-purple-600/90",
      delay: 0.1,
      likes: "1.8K",
      language: "Spanish"
    },
    {
      id: 3,
      icon: <FaChartLine className="w-8 h-8 text-white" />,
      title: "Track Your Progress",
      description: "See your improvement over time with detailed analytics and personalized insights.",
      image: "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/Screenshot%202025-08-11%20204130.png",
      color: "blue",
      gradient: "from-blue-500/80 to-blue-700/80",
      hoverGradient: "from-blue-400/90 to-blue-600/90",
      delay: 0.2,
      likes: "3.1K",
      language: "French"
    },
    {
      id: 4,
      icon: <FaHeadphones className="w-8 h-8 text-white" />,
      title: "Fun, Game-Like Quizzes",
      description: "Test your knowledge with engaging quizzes based on the songs you're learning from.",
      image: "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/Screenshot%202025-08-11%20205120.png",
      color: "red",
      gradient: "from-red-500/80 to-red-700/80",
      hoverGradient: "from-red-400/90 to-red-600/90",
      delay: 0.3,
      likes: "1.9K",
      language: "English"
    }
  ];

  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const isInView = useInView(sectionRef, { once: false, amount: 0.3 });
  const controls = useAnimation();

  // Animate cards when section comes into view
  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  // Set up parallax effect
  useEffect(() => {
    if (!sectionRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const section = sectionRef.current;
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const moveX = (clientX - centerX) / 50;
      const moveY = (clientY - centerY) / 50;

      // Apply parallax to each card
      cardsRef.current.forEach((card, index) => {
        if (!card) return;
        
        // Different intensity for each card to create depth
        const intensity = 1 - (index * 0.1);
        
        gsap.to(card, {
          x: moveX * intensity,
          y: moveY * intensity,
          rotateX: -moveY * 0.1 * intensity,
          rotateY: moveX * 0.1 * intensity,
          duration: 0.5,
          ease: "power2.out"
        });
      });
    };

    // Reset positions when mouse leaves
    const handleMouseLeave = () => {
      cardsRef.current.forEach((card) => {
        if (!card) return;
        
        gsap.to(card, {
          x: 0,
          y: 0,
          rotateX: 0,
          rotateY: 0,
          duration: 0.5,
          ease: "power2.out"
        });
      });
    };

    const sectionElement = sectionRef.current;
    sectionElement.addEventListener('mousemove', handleMouseMove);
    sectionElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      sectionElement.removeEventListener('mousemove', handleMouseMove);
      sectionElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.8,
      rotateX: 15
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.8
      }
    }
  };

  return (
    <section ref={sectionRef} className="py-24 px-6 relative overflow-hidden bg-black" id="features">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 -z-10">
        {/* Red Gradient Orbs */}
        <motion.div 
          className="absolute top-0 left-0 w-96 h-96 bg-red-500/10 rounded-full filter blur-[100px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-0 right-0 w-96 h-96 bg-red-600/10 rounded-full filter blur-[100px]"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-red-400/5 rounded-full filter blur-[100px]"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4
          }}
        />
        
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 
            className="text-4xl md:text-5xl font-bold mb-4 relative inline-block"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="bg-gradient-to-r from-red-400 via-red-300 to-white bg-clip-text text-transparent">
              Why Choose Amalie Ai
            </span>
            <motion.span 
              className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-red-400 to-transparent rounded-full"
              initial={{ width: 0 }}
              whileInView={{ width: '100%' }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.5 }}
            ></motion.span>
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Our unique approach combines the joy of music with effective language learning techniques
          </motion.p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.id}
              ref={el => cardsRef.current[index] = el}
              className="group relative bg-gray-900/50 backdrop-blur-xl rounded-2xl overflow-hidden border border-gray-800/50 transition-all duration-500 transform perspective-1000 hover:shadow-2xl cursor-pointer"
              variants={cardVariants}
              whileHover={{ 
                scale: 1.05,
                y: -10,
                transition: { duration: 0.3 }
              }}
            >
              {/* Song Card Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${feature.gradient} opacity-60 group-hover:opacity-40 transition-opacity duration-500`}></div>
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <motion.div
                    className="w-16 h-16 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center border border-white/30"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaPlay className="w-6 h-6 text-white ml-1" />
                  </motion.div>
                </div>

                {/* Language Badge */}
                <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-lg rounded-full text-xs font-medium text-white border border-white/20">
                  {feature.language}
                </div>

                {/* Feature Icon */}
                <div className="absolute top-3 left-3 w-10 h-10 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center border border-white/30">
                  {feature.icon}
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6 relative">
                {/* Glass Reflection Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-b-2xl pointer-events-none"></div>
                
                <div className="relative z-10">
                  {/* Title */}
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-red-300 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-300 text-sm leading-relaxed mb-4 group-hover:text-gray-200 transition-colors duration-300">
                    {feature.description}
                  </p>

                  {/* Song Card Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-700/50">
                    <div className="flex items-center space-x-3">
                      <button className="flex items-center space-x-1 text-gray-400 hover:text-red-400 transition-colors duration-200">
                        <FaHeart className="w-4 h-4" />
                        <span className="text-sm font-medium">{feature.likes}</span>
                      </button>
                      <div className="flex items-center space-x-1 text-gray-400">
                        <FaLanguage className="w-4 h-4" />
                        <span className="text-sm">Learn</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <FaStar className="w-4 h-4 text-yellow-400" />
                      <FaStar className="w-4 h-4 text-yellow-400" />
                      <FaStar className="w-4 h-4 text-yellow-400" />
                      <FaStar className="w-4 h-4 text-yellow-400" />
                      <FaStar className="w-4 h-4 text-gray-600" />
                    </div>
                  </div>
                </div>

                {/* Hover Glow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.hoverGradient} opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-b-2xl pointer-events-none`}></div>
              </div>

              {/* Animated Border on Hover */}
              <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-red-400/50 transition-all duration-500"></div>
              
              {/* Floating Particles on Hover */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-2 h-2 rounded-full ${
                      feature.color === 'red' ? 'bg-red-400' :
                      feature.color === 'purple' ? 'bg-purple-400' :
                      feature.color === 'blue' ? 'bg-blue-400' :
                      'bg-yellow-400'
                    }`}
                    initial={{ 
                      x: Math.random() * 100 + '%', 
                      y: Math.random() * 100 + '%', 
                      opacity: 0,
                      scale: 0
                    }}
                    whileHover={{ 
                      y: [null, '-20%'],
                      opacity: [0, 1, 0],
                      scale: [0, Math.random() + 0.5, 0]
                    }}
                    transition={{ 
                      duration: 2 + Math.random() * 2,
                      repeat: Infinity,
                      repeatType: "loop",
                      delay: Math.random() * 2
                    }}
                  />
                ))}
              </div>

              {/* Shine Effect on Hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Floating Musical Notes */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-red-500/10 pointer-events-none"
            initial={{ 
              x: Math.random() * 100 + '%', 
              y: Math.random() * 100 + '%',
              rotate: Math.random() * 360,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{ 
              y: [null, '-30%'],
              rotate: [null, Math.random() * 360 + 180],
              opacity: [0.1, 0.3, 0.1]
            }}
            transition={{ 
              duration: Math.random() * 15 + 20, 
              repeat: Infinity,
              ease: "linear"
            }}
            style={{ 
              fontSize: `${Math.random() * 3 + 2}rem`,
              filter: 'blur(1px)'
            }}
          >
            {['♪', '♫', '♬', '♩', '♭', '♮', '♯'][Math.floor(Math.random() * 7)]}
          </motion.div>
        ))}
      </div>
      
      {/* Custom Styles */}
      <style jsx>{`
        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(239, 68, 68, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(239, 68, 68, 0.05) 1px, transparent 1px);
          background-size: 40px 40px;
          animation: grid-drift 30s linear infinite;
        }
        
        @keyframes grid-drift {
          0% { transform: translate(0, 0); }
          100% { transform: translate(40px, 40px); }
        }
        
        .perspective-1000 {
          perspective: 1000px;
        }
        
        @keyframes shimmer {
          0% {
            background-position: -100% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        .shimmer-text {
          background: linear-gradient(90deg, 
            rgba(239, 68, 68, 0.8) 0%, 
            rgba(255, 255, 255, 1) 25%, 
            rgba(239, 68, 68, 0.8) 50%, 
            rgba(255, 255, 255, 1) 75%, 
            rgba(239, 68, 68, 0.8) 100%);
          background-size: 200% auto;
          color: transparent;
          background-clip: text;
          -webkit-background-clip: text;
          animation: shimmer 3s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default Features;